import React, { useState, useEffect } from 'react';
import accmod1 from '../assets/accmod1.jpg';
import accmod2 from '../assets/accmod2.jpg';
import accmod3 from '../assets/accmod3.jpg';
import accmod4 from '../assets/accmod4.jpg';
import accmod5 from '../assets/accmod5.jpg';
import profileCombo from '../assets/profilecombo.jpg';
import api from '../api';
import ImageCropper from './ImageCropper';
import { Tooltip } from 'react-tooltip';
import 'react-tooltip/dist/react-tooltip.css';

const TABS = [
  { key: 'photo', label: 'Profile Photo', img: accmod1 },
  { key: 'username', label: 'Username', img: accmod2 },
  { key: 'password', label: 'Password', img: accmod3 },
  { key: 'delete', label: 'Delete Account', img: accmod4 },
  { key: 'logout', label: 'Logout', img: accmod5 },
];

const AccountModal = ({ onClose }) => {
  const [activeTab, setActiveTab] = useState('photo');
  const [username, setUsername] = useState('');
  const [usernameMsg, setUsernameMsg] = useState('');
  const [oldPassword, setOldPassword] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [passwordMsg, setPasswordMsg] = useState('');
  const [photo, setPhoto] = useState(null);
  const [photoMsg, setPhotoMsg] = useState('');
  const [deleteConfirm, setDeleteConfirm] = useState(false);
  const [showCropper, setShowCropper] = useState(false);
  const [imageToCrop, setImageToCrop] = useState(null);
  const [profileImgUrl, setProfileImgUrl] = useState(null);
  const [isPhotoLoading, setIsPhotoLoading] = useState(false);
  const [gender, setGender] = useState('unspecified');
  const [showImageModal, setShowImageModal] = useState(false);

  // Fetch the user's profile photo and gender on mount or after upload
  useEffect(() => {
    if (activeTab === 'photo') {
      setIsPhotoLoading(true);
      // Fetch user gender (if available)
      api.get('/users/me').then(res => {
        setGender(res.data.gender || 'unspecified');
      }).catch(() => setGender('unspecified'));
      // Fetch profile image from the correct endpoint
      api.get('/users/profile-image', { responseType: 'blob' })
        .then(res => {
          const url = URL.createObjectURL(res.data);
          setProfileImgUrl(url);
          setIsPhotoLoading(false);
        })
        .catch(() => {
          // Gender-based fallback
          let fallbackImg = profileCombo;
          if (gender === 'male') fallbackImg = require('../assets/profilemale.jpg');
          else if (gender === 'female') fallbackImg = require('../assets/profilefemale.jpg');
          setProfileImgUrl(fallbackImg);
          setIsPhotoLoading(false);
        });
    }
  }, [activeTab, photoMsg]);

  // Profile photo upload handler
  const handlePhotoUpload = async () => {
    setPhotoMsg('');
    if (!photo) {
      setPhotoMsg('Please select a photo to upload.');
      return;
    }
    setIsPhotoLoading(true);
    const formData = new FormData();
    formData.append('photo', photo, 'profile.jpg');
    formData.append('gender', gender); // Always send gender
    try {
      await api.post('/users/profile-photo', formData, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });
      setPhotoMsg('Profile photo updated!');
      // Refetch image
      api.get('/users/profile-image', { responseType: 'blob' })
        .then(res => {
          const url = URL.createObjectURL(res.data);
          setProfileImgUrl(url);
          setIsPhotoLoading(false);
        })
        .catch(() => setIsPhotoLoading(false));
    } catch (err) {
      setPhotoMsg(err.response?.data?.error || 'Failed to upload photo.');
      setIsPhotoLoading(false);
    }
  };

  const handleFileChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      const reader = new FileReader();
      reader.onload = (ev) => {
        setImageToCrop(ev.target.result);
        setShowCropper(true);
      };
      reader.readAsDataURL(file);
    }
  };

  const handleCropComplete = (croppedBlob) => {
    setPhoto(croppedBlob);
    setShowCropper(false);
    setImageToCrop(null);
  };

  const handleCropCancel = () => {
    setShowCropper(false);
    setImageToCrop(null);
  };

  // Username change handler
  const handleUsernameChange = async () => {
    setUsernameMsg('');
    if (!username) {
      setUsernameMsg('Please enter a new username.');
      return;
    }
    try {
      await api.post('/users/change-username', { username });
      setUsernameMsg('Username updated!');
    } catch (err) {
      setUsernameMsg(err.response?.data?.error || 'Failed to update username.');
    }
  };

  // Password change handler
  const handlePasswordChange = async () => {
    setPasswordMsg('');
    if (!oldPassword || !newPassword || !confirmPassword) {
      setPasswordMsg('Please fill in all password fields.');
      return;
    }
    if (newPassword !== confirmPassword) {
      setPasswordMsg('New passwords do not match.');
      return;
    }
    try {
      await api.post('/users/change-password', { oldPassword, newPassword });
      setPasswordMsg('Password updated!');
      setOldPassword('');
      setNewPassword('');
      setConfirmPassword('');
    } catch (err) {
      setPasswordMsg(err.response?.data?.error || 'Failed to update password.');
    }
  };

  // Delete account handler
  const handleDeleteAccount = async () => {
    try {
      await api.post('/users/delete-account');
      localStorage.removeItem('uma_token');
      window.location.href = '/umaf/login';
    } catch (err) {
      alert(err.response?.data?.error || 'Failed to delete account.');
    }
  };

  // Logout handler
  const handleLogout = () => {
    localStorage.removeItem('uma_token');
    window.location.href = '/umaf/login';
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-80 flex items-center justify-center z-50">
      <div className="bg-green-950 border border-green-500 p-6 rounded-xl shadow-lg w-full max-w-lg max-h-[90vh] overflow-y-auto relative font-mono">
        <button onClick={onClose} className="absolute top-2 right-2 text-green-400 z-10 text-xl">✕</button>
        <h2 className="text-2xl text-green-400 mb-4 text-center glitch-text">Account Control Terminal</h2>
        <div className="flex gap-2 mb-4 border-b border-green-700 pb-2">
          {TABS.map(tab => (
            <button
              key={tab.key}
              className={`px-3 py-1 rounded-t text-sm font-bold transition-all duration-200 ${activeTab === tab.key ? 'bg-green-800 text-green-200 border-b-2 border-green-400' : 'bg-green-950 text-green-400 hover:bg-green-900'}`}
              onClick={() => setActiveTab(tab.key)}
            >
              {tab.label}
            </button>
          ))}
        </div>
        <div className="mt-2 flex flex-col items-center">
          <img src={TABS.find(t => t.key === activeTab).img} alt="sci-fi" className="mb-4 rounded shadow-lg max-w-full max-h-48 object-cover" />
          {activeTab === 'photo' && (
            <div className="space-y-3 w-full flex flex-col items-center">
              <div className="text-green-300 text-center">
                {isPhotoLoading ? (
                  <span>Interstellar image transition in progress… This may take a minute or two. Please standby for confirmation.</span>
                ) : (
                  <img
                    src={profileImgUrl}
                    alt="Profile"
                    className="shadow-lg w-32 h-32 object-cover border-2 border-green-400 mx-auto mb-2 cursor-pointer rounded-none"
                    style={{ borderRadius: 0 }}
                    onClick={() => setShowImageModal(true)}
                    onError={e => {
                      let fallbackImg = profileCombo;
                      if (gender === 'male') fallbackImg = require('../assets/profilemale.jpg');
                      else if (gender === 'female') fallbackImg = require('../assets/profilefemale.jpg');
                      e.target.onerror = null; e.target.src = fallbackImg;
                    }}
                  />
                )}
              </div>
              <label htmlFor="account-photo-upload" className="block mb-2">
                Profile Photo
                <span
                  data-tooltip-id="account-photo-tip"
                  data-tooltip-content="Upload a clear, well-lit photo of your face (no sunglasses or hats). Use a square image, at least 1024x1024px, for best AI results. Backgrounds don't matter—AI will place you on IKOBU Station!"
                  className="ml-2 cursor-pointer text-green-400"
                  style={{ fontWeight: 'bold', fontSize: '1.2em' }}
                >
                  ⓘ
                </span>
              </label>
              <input type="file" id="account-photo-upload" accept="image/*" onChange={handleFileChange} className="input-terminal" />
              <Tooltip id="account-photo-tip" place="top" />
              <button className="btn-secondary w-full" onClick={async () => {
                setIsPhotoLoading(true);
                await handlePhotoUpload();
                // The effect will reload the image
              }}>Upload New Photo</button>
              {photoMsg && <div className="text-yellow-400 text-xs mt-2">{photoMsg}</div>}
            </div>
          )}
          {activeTab === 'username' && (
            <div className="space-y-3 w-full flex flex-col items-center">
              <input type="text" value={username} onChange={e => setUsername(e.target.value)} placeholder="New Username" className="input-terminal w-full" />
              <button className="btn-secondary w-full" onClick={handleUsernameChange}>Save Username</button>
              {usernameMsg && <div className="text-yellow-400 text-xs mt-2">{usernameMsg}</div>}
            </div>
          )}
          {activeTab === 'password' && (
            <div className="space-y-3 w-full flex flex-col items-center">
              <input type="password" value={oldPassword} onChange={e => setOldPassword(e.target.value)} placeholder="Old Password" className="input-terminal w-full" />
              <input type="password" value={newPassword} onChange={e => setNewPassword(e.target.value)} placeholder="New Password" className="input-terminal w-full" />
              <input type="password" value={confirmPassword} onChange={e => setConfirmPassword(e.target.value)} placeholder="Confirm New Password" className="input-terminal w-full" />
              <button className="btn-secondary w-full" onClick={handlePasswordChange}>Change Password</button>
              {passwordMsg && <div className="text-yellow-400 text-xs mt-2">{passwordMsg}</div>}
            </div>
          )}
          {activeTab === 'delete' && (
            <div className="space-y-3 w-full flex flex-col items-center">
              <div className="text-red-400 font-bold text-center">Warning: This will mark your account for deletion. You will lose access after confirmation. Data is not immediately deleted.</div>
              {!deleteConfirm ? (
                <button className="btn-danger w-full" onClick={() => setDeleteConfirm(true)}>Delete My Account</button>
              ) : (
                <div className="flex flex-col gap-2 w-full">
                  <button className="btn-danger w-full" onClick={handleDeleteAccount}>Confirm Delete</button>
                  <button className="btn-secondary w-full" onClick={() => setDeleteConfirm(false)}>Cancel</button>
                </div>
              )}
            </div>
          )}
          {activeTab === 'logout' && (
            <div className="space-y-3 text-center w-full">
              <div className="text-green-300">Ready to log out?</div>
              <button className="btn-primary w-full" onClick={handleLogout}>Logout</button>
            </div>
          )}
        </div>
        {showCropper && imageToCrop && (
          <ImageCropper
            imageSrc={imageToCrop}
            onCropComplete={handleCropComplete}
            onCancel={handleCropCancel}
            aspect={1}
          />
        )}
        {showImageModal && (
          <div className="fixed inset-0 bg-black bg-opacity-90 flex items-center justify-center z-50">
            <div className="relative">
              <button onClick={() => setShowImageModal(false)} className="absolute top-2 right-2 text-green-400 text-2xl z-10">✕</button>
              <img src={profileImgUrl} alt="Full Profile" className="max-w-[90vw] max-h-[90vh] border-4 border-green-400 bg-black" style={{ borderRadius: 0 }} />
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default AccountModal; 